import { type MRT_ColumnDef, useMaterialReactTable } from 'material-react-table';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { WeightChargeDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum, formatMoney } from 'src/utils/utility';
import { formatDateString, formatTimeDisplay } from 'src/utils/date';

export const TableMain = styled(Box)({
  '.MuiTableCell-head:first-of-type': {
    width: '40px',
  },
});

const useRateCardWeightColumns = (): MRT_ColumnDef<WeightChargeDto>[] => {
  const columns = [
    {
      accessorKey: 'legType',
      header: 'Leg',
      enableEditing: false,
      accessorFn: (row: WeightChargeDto) => <span className='capitalize'>{beautifyEnum(row?.legType)}</span>,
    },
    {
      accessorKey: 'chargeName',
      header: 'Charge Name / Category / [min Wt/Vol - max Wt/Vol]',
      enableEditing: false,
      accessorFn: (row: WeightChargeDto) => (
        <span className='capitalize'>
          {beautifyEnum(row.chargeName)} / {beautifyEnum(row.chargeCategory)} / [{row.minimumChargeableWeight} - {row.maximumChargeableWeight}]
        </span>
      ),
    },
    {
      enableEditing: false,
      accessorKey: 'validityPeriod',
      header: 'Validity Period',
      accessorFn: (row: WeightChargeDto) => {
        const startDate = row?.startDate;
        const endDate = row?.endDate;

        if (!startDate && !endDate) {
          return <span className='text-center block w-full'>-</span>;
        }

        return <span>{`${formatDateString(startDate)} - ${formatDateString(endDate)}`}</span>;
      },
    },
    {
      enableEditing: false,
      accessorKey: 'timeRange',
      header: 'Time Range',
      accessorFn: (row: WeightChargeDto) => {
        const startTime = row.startTime;
        const endTime = row.endTime;

        if (!startTime && !endTime) {
          return <span className='text-center block w-full'>-</span>;
        }

        return (
          <div className='flex flex-col'>
            <span>Start Time: {formatTimeDisplay(startTime)}</span>
            <span>End Time: {formatTimeDisplay(endTime)}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'unitPrice',
      header: 'Rate Card Price / Unit',
      enableEditing: false,
      accessorFn: (row: WeightChargeDto) => (
        <span className='capitalize'>
          {formatMoney(row?.unitPrice, row?.currency)} / {row?.chargeUnit}
        </span>
      ),
    },
    {
      accessorKey: 'minimumPrice',
      header: 'Rate Card Minimum Price',
      enableEditing: false,
      accessorFn: (row: WeightChargeDto) => <span className='capitalize'>{formatMoney(row?.minimumPrice, row?.currency)}</span>,
    },
  ];
  return columns;
};

export interface ITableRateCardWeightProps {
  data: WeightChargeDto[];
}

export const TableRateCardWeight = ({ data }: ITableRateCardWeightProps) => {
  const columns = useRateCardWeightColumns();

  const table = useMaterialReactTable({
    data,
    enableRowNumbers: false,
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: false,
    editDisplayMode: 'table',
    muiTableHeadProps: {
      sx: {
        '& .MuiTableCell-head': {
          '&:first-of-type': {
            width: '100px',
            textAlign: 'center',
          },
        },
      },
    },
    columns,
  });
  return <CustomTable table={table} />;
};
